import { inject, Injectable } from '@angular/core';
import { AnalysisService } from './analysis.service';
import { DemographicsOptionsService } from '../components/filters-report/service/demographics-options.service';
import { FinalAssessmentsApiResponse } from '../infrastructure/analysis.api.types';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Option } from '../components/filters-report/components/FiltroSelectComponent/filtro-select.component';
import { RISK_ORDER, Risk } from '@/app/utils/risk-colors';

type SimpleDatum = { name: string; value: number };
type MultiSeries = Array<{ name: string; series: Array<{ name: string; value: number }> }>;

@Injectable({ providedIn: 'root' })
export class InitialChartDataService {
  private analysis = inject(AnalysisService);
  private optionsService = inject(DemographicsOptionsService);

  getAgeRangeDistribution(campaignId: string): Observable<MultiSeries> {
    return this.analysis.getFinalAssessmentsByGroup({
      campaign: campaignId,
      guideId: 'guia-ii',
      groupBy: 'ageRange',
    }).pipe(
      catchError(() => of([])),
      map(apiData => this.toAgeRangeGroupedSeries(apiData))
    );
  }

  getGenderDistribution(campaignId: string): Observable<SimpleDatum[]> {
    return this.analysis.getFinalAssessmentsByGroup({
      campaign: campaignId,
      guideId: 'guia-ii',
      groupBy: 'gender',
    }).pipe(
      catchError(() => of([])),
      map(apiData => this.toFullSeries(apiData, this.optionsService.getGender(), 'gender'))
    );
  }

  private toAgeRangeGroupedSeries(apiData: FinalAssessmentsApiResponse | undefined): MultiSeries {
    const ageRangeOptions = this.optionsService.getAgeRange();
    const riskLevels: readonly Risk[] = RISK_ORDER;

    const dataMap = new Map<string, Map<Risk, number>>();
    ageRangeOptions.forEach(ageOpt => {
      const riskMap = new Map<Risk, number>();
      riskLevels.forEach(risk => riskMap.set(risk, 0));
      dataMap.set(ageOpt.label, riskMap);
    });

    if (Array.isArray(apiData)) {
      for (const item of apiData) {
        const ageRangeLabel = ageRangeOptions.find(o => o.value === item.ageRange)?.label;
        const riskLevel = item.finalAssessment as Risk;
        const total = Number(item.totalRespondents ?? 0);

        if (ageRangeLabel && dataMap.has(ageRangeLabel)) {
          const riskMap = dataMap.get(ageRangeLabel)!;
          if (riskMap.has(riskLevel)) {
            riskMap.set(riskLevel, (riskMap.get(riskLevel) || 0) + total);
          }
        }
      }
    }

    return Array.from(dataMap.entries()).map(([ageRangeName, riskMap]) => ({
      name: ageRangeName,
      series: Array.from(riskMap.entries()).map(([riskName, value]) => ({
        name: riskName,
        value: value,
      })),
    }));
  }

  private toFullSeries(apiData: FinalAssessmentsApiResponse | undefined, options: Option[], groupKey: string): SimpleDatum[] {
    const apiDataMap = new Map<string, number>();

    if (Array.isArray(apiData)) {
      for (const item of apiData) {
        const groupName = item[groupKey as keyof typeof item];
        if (groupName) {
          const currentTotal = apiDataMap.get(String(groupName)) ?? 0;
          apiDataMap.set(String(groupName), currentTotal + Number(item.totalRespondents ?? 0));
        }
      }
    }

    return options.map(opt => ({
      name: opt.label,
      value: apiDataMap.get(opt.value) ?? 0
    }));
  }
}
